import React from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';

import LandingSection from '../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../MyComponents/LandingSections/SectionEyebrow';
import HighlightMark from '../../MyComponents/LandingSections/HighlightMark';
import { featureList, getIntlCount, replaceValues } from '../../common/features';
import { CARD, PILL_PRIMARY, PILL_SECONDARY, TEXT_LINK, cx } from '../Home/HomeDashboard/dashboardStyles';
import { COLORS2, REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

export const ONE_COL = '@media (max-width: 860px)';

// Styles the onboarding stages share: the hero, the card, the pills, the plan
// feature list, the billing toggles and the form field.
export const useOnboardingStyles = makeStyles(() => ({
    intro: {
        maxWidth: '820px',
        margin: '0 auto',
        textAlign: 'center',
    },
    eyebrowRow: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '22px',
    },
    title: {
        margin: '0 0 18px',
        fontSize: 'clamp(2rem, 4.4vw, 3.4rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        textWrap: 'balance',
        '& p': {
            margin: 0,
        },
    },
    lead: {
        maxWidth: '680px',
        margin: '0 auto',
        color: REDESIGN_COLORS.body,
        fontSize: '1.06rem',
        lineHeight: 1.6,
    },
    card: {
        ...CARD,
        padding: 'clamp(22px, 2.8vw, 34px)',
    },
    centered: {
        maxWidth: '640px',
        margin: '0 auto',
        textAlign: 'center',
    },
    cardTitle: {
        margin: '0 0 14px',
        fontSize: '1.2rem',
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    text: {
        margin: '0 0 12px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.98rem',
        lineHeight: 1.6,
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        marginTop: '20px',
    },
    primary: {
        ...PILL_PRIMARY,
        minHeight: '52px',
        padding: '0 26px',
    },
    secondary: {
        ...PILL_SECONDARY,
        minHeight: '52px',
        padding: '0 24px',
    },
    danger: {
        ...PILL_SECONDARY,
        minHeight: '52px',
        padding: '0 24px',
        borderColor: 'rgba(243, 83, 37, 0.4)',
        color: COLORS2.virtualRed,
        '&:hover': {
            borderColor: COLORS2.virtualRed,
            color: COLORS2.virtualRed,
        },
    },
    link: TEXT_LINK,
    trust: {
        margin: '16px 0 0',
        color: REDESIGN_COLORS.muted,
        fontSize: '0.9rem',
        fontWeight: 600,
    },
    layout: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 0.85fr)',
        gap: '22px',
        alignItems: 'start',
        [ONE_COL]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    // Plan features, the same ticks the pricing section draws.
    bullets: {
        display: 'grid',
        gap: '10px',
        margin: 0,
        padding: 0,
        listStyle: 'none',
        color: REDESIGN_COLORS.dark,
        fontSize: '0.94rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    bullet: {
        display: 'flex',
        gap: '10px',
    },
    bulletOff: {
        display: 'flex',
        gap: '10px',
        color: REDESIGN_COLORS.muted,
        fontWeight: 500,
    },
    tick: {
        color: REDESIGN_COLORS.primary,
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    tickOff: {
        color: REDESIGN_COLORS.line,
    },
    // Billing toggles, as on the pricing section.
    toggles: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '10px',
        margin: '0 0 22px',
    },
    group: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px',
        border: `1px solid ${REDESIGN_COLORS.line}`,
        borderRadius: '999px',
        background: REDESIGN_COLORS.toggleTrack,
    },
    toggle: {
        minHeight: '38px',
        padding: '0 16px',
        border: 0,
        borderRadius: '999px',
        background: 'none',
        color: REDESIGN_COLORS.body,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '0.88rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        cursor: 'pointer',
    },
    toggleOn: {
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.primary,
    },
    saveBadge: {
        display: 'inline-block',
        marginLeft: '6px',
        padding: '2px 7px',
        borderRadius: '999px',
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.highlight,
        fontSize: '0.68rem',
    },
    // Form field, as on the contact page.
    field: {
        display: 'grid',
        gap: '6px',
        textAlign: 'left',
    },
    label: {
        color: REDESIGN_COLORS.dark,
        fontSize: '0.86rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    input: {
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '50px',
        padding: '12px 16px',
        border: `1px solid ${REDESIGN_COLORS.line}`,
        borderRadius: '14px',
        background: REDESIGN_COLORS.cream,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '1rem',
        fontWeight: 600,
        color: REDESIGN_COLORS.dark,
        outline: 'none',
        '&:focus': {
            borderColor: REDESIGN_COLORS.primary,
            boxShadow: '0 0 0 3px rgba(16, 169, 179, 0.15)',
        },
        '&:disabled': {
            color: REDESIGN_COLORS.muted,
        },
    },
}));

/**
 * A plan's real capabilities as a tick list, from the same public payload and
 * featureList/replaceValues pair the pricing section uses. Pass either the
 * plan `type` with the public `subscriptions`, or the resolved capabilities
 * as `data` (what getSubscriptionData returns for an account).
 */
export const PlanFeatures = ({ type, subscriptions, data }) => {
    const classes = useOnboardingStyles();
    const intl = useIntl();
    const plan = data || (subscriptions && subscriptions[type]);
    if (!plan) return null;

    const list = featureList.map((f) => ({ ...f }));
    replaceValues(plan, list);

    return (
        <ul className={classes.bullets}>
            {list.map((f) => {
                const isCount = typeof f.defaultValue !== 'boolean';
                const included = isCount ? f.defaultValue !== 0 : f.defaultValue;
                const formatObj = { [f.name]: getIntlCount(intl, f.defaultValue) };
                const label = intl.formatMessage(
                    { id: (isCount ? 'redesign.subscriptions.feature.' : 'subscriptions.feature.') + f.name + '.name' },
                    formatObj
                );
                const value = isCount
                    ? intl.formatMessage({ id: 'subscriptions.feature.' + f.name + '.name' }, formatObj)
                    : null;
                return (
                    <li key={f.name} className={included ? classes.bullet : classes.bulletOff}>
                        <span aria-hidden="true" className={included ? classes.tick : classes.tickOff}>
                            {included ? '✓' : '—'}
                        </span>
                        {value ? label + ': ' + value : label}
                    </li>
                );
            })}
        </ul>
    );
};

/** The monthly/annual and RON/EUR switches. */
export const BillingToggles = ({ isAnnual, isEur, onAnnual, onEur }) => {
    const classes = useOnboardingStyles();
    const intl = useIntl();
    const t = (id) => intl.formatMessage({ id: `redesign.home.pricing.${id}` });
    const toggle = (on, onClick, children) => (
        <button type="button" aria-pressed={on} onClick={onClick} className={cx(classes.toggle, on && classes.toggleOn)}>
            {children}
        </button>
    );

    return (
        <div className={classes.toggles}>
            <div role="group" aria-label={t('billing.aria')} className={classes.group}>
                {toggle(!isAnnual, () => onAnnual(false), t('billing.monthly'))}
                {toggle(
                    isAnnual,
                    () => onAnnual(true),
                    <>
                        {t('billing.annual')}
                        <span className={classes.saveBadge}>{t('billing.saveBadge')}</span>
                    </>
                )}
            </div>
            <div role="group" aria-label={t('currency.aria')} className={classes.group}>
                {toggle(!isEur, () => onEur(false), 'RON')}
                {toggle(isEur, () => onEur(true), 'EUR')}
            </div>
        </div>
    );
};

/**
 * The frame every onboarding stage sits in: eyebrow, headline (a node; `mark`
 * is available through `values`), lead, then the stage's own content.
 */
const OnboardingShell = ({ eyebrowId = 'redesign.onboarding.eyebrow', title, lead, maxWidth = 1080, children }) => {
    const classes = useOnboardingStyles();
    const intl = useIntl();

    return (
        <>
            <LandingSection id="top" maxWidth={maxWidth} paddingTop={46} paddingBottom={24}>
                <div className={classes.intro}>
                    <div className={classes.eyebrowRow}>
                        <SectionEyebrow>{intl.formatMessage({ id: eyebrowId })}</SectionEyebrow>
                    </div>
                    <h1 className={classes.title}>{title}</h1>
                    {lead ? <p className={classes.lead}>{lead}</p> : null}
                </div>
            </LandingSection>
            <LandingSection maxWidth={maxWidth} paddingTop={16} paddingBottom={64}>
                {children}
            </LandingSection>
        </>
    );
};

export const mark = (chunks) => <HighlightMark>{chunks}</HighlightMark>;

export default OnboardingShell;
